<template>
  <div>
    <TopBar currentTitle="Partner Campaigns">
      <b-button type="is-primary" size="is-small" icon-left="plus" @click="createNewCampaign">Add Campaign</b-button>
    </TopBar>
    <div class="page-content">
    <h1 class="page-title">Partner Campaigns</h1>
    <p class="page-subtitle mb-4">{{ campaigns.length }} total</p>

    <!-- Table Card -->
    <div class="feeds-card">
      <div class="feeds-card-body compact">
        <b-table
          :data="campaigns"
          :hoverable="true"
          default-sort="created_at"
          default-sort-direction="desc"
          @click="openCampaign">

          <b-table-column field="partner_name" label="Partner Name" sortable v-slot="props">
            <p class="has-text-weight-bold">{{ props.row.partner_name }}</p>
          </b-table-column>

          <b-table-column field="campaign_name" label="Campaign Name" sortable v-slot="props">
            {{ props.row.campaign_name }}
          </b-table-column>

          <b-table-column field="deep_link_path" label="Deep Link Path" sortable v-slot="props">
            <code class="deep-link-code">{{ props.row.deep_link_path }}</code>
          </b-table-column>

          <b-table-column field="status" width=100 label="Status" sortable centered v-slot="props">
            <FeedsStatusBadge :status="props.row.status" />
          </b-table-column>

          <b-table-column field="start_date" label="Date Range" sortable v-slot="props">
            {{ formatDateRange(props.row.start_date, props.row.end_date) }}
          </b-table-column>

          <b-table-column field="created_at" width=100 label="Created" sortable centered v-slot="props">
            <span class="date-tag">{{ formatDate(props.row.created_at) }}</span>
          </b-table-column>

          <b-table-column field="options" custom-key="actions" width=20 v-slot="props">
            <b-dropdown aria-role="list" position="is-bottom-left" @click.native.stop>
              <template #trigger>
                <b-button icon-right="dots-vertical" />
              </template>
              <b-dropdown-item aria-role="listitem" @click="duplicateCampaign(props.row)">
                <b-icon size="is-small" icon="content-copy"></b-icon>
                Duplicate
              </b-dropdown-item>
              <b-dropdown-item class="has-text-danger" aria-role="listitem" @click="deleteCampaign(props.row)">
                <b-icon size="is-small" icon="delete"></b-icon>
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered py-4">No partner campaigns found</div>
          </template>
        </b-table>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import db from "@/firebase/db"
import FeedsStatusBadge from '@/components/common/FeedsStatusBadge.vue'
import TopBar from '@/components/layout/TopBar.vue'

const SCROLL_STATE_KEY = 'feeds_list_state_PartnerCampaignListView'

export default {
  name: 'PartnerCampaignListView',
  components: {
    FeedsStatusBadge,
    TopBar
  },
  data() {
    return {
      campaigns: [],
      scrollRestored: false
    }
  },
  watch: {
    campaigns() {
      if (!this.scrollRestored) {
        this.scrollRestored = true
        this.$nextTick(() => {
          const savedState = sessionStorage.getItem(SCROLL_STATE_KEY)
          if (savedState) {
            const state = JSON.parse(savedState)
            if (state.scrollY > 0) {
              setTimeout(() => window.scrollTo(state.scrollX, state.scrollY), 50)
            }
          }
        })
      }
    }
  },
  firestore: {
    campaigns: db.collection('partner_campaigns').orderBy("created_at", "desc")
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '-'
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString()
    },
    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return '-'
      const start = startDate.toDate ? startDate.toDate() : new Date(startDate)
      const end = endDate.toDate ? endDate.toDate() : new Date(endDate)
      const options = { month: 'short', day: 'numeric', year: 'numeric' }
      return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`
    },
    createNewCampaign() {
      const now = new Date()
      const docRef = db.collection("partner_campaigns").doc()
      docRef.set({
        id: docRef.id,
        created_at: now,
        updated_at: now,
        status: 'draft',
        partner_id: '',
        partner_name: '',
        campaign_name: '',
        deep_link_path: 'campaign-',
        hero_image_url: '',
        badge_text: '',
        hero_title: '',
        subtitle: '',
        benefits: [],
        logo_url: '',
        brand_color_hex: '#000000',
        offering_id: '',
        start_date: null,
        end_date: null,
        welcome_title: '',
        welcome_message: '',
        welcome_image_url: ''
      })
      .then(() => {
        this.$router.push({ name: 'PartnerCampaignView', params: { id: docRef.id } })
      })
      .catch((error) => {
        console.error("Error adding document: ", error);
        this.$buefy.toast.open({type: 'is-danger', message: `Error creating campaign: ${error}`})
      });
    },
    openCampaign(campaign) {
      this.$router.push({ name: 'PartnerCampaignView', params: { id: campaign.id } })
    },
    duplicateCampaign(campaign) {
      this.$buefy.dialog.confirm({
        title: 'Duplicate Campaign',
        message: `Create a copy of <b>"${campaign.partner_name}"</b>?`,
        confirmText: 'Duplicate',
        type: 'is-warning',
        hasIcon: true,
        onConfirm: () => {
          const now = new Date()
          const docRef = db.collection("partner_campaigns").doc()
          const newCampaign = {
            ...campaign,
            id: docRef.id,
            created_at: now,
            updated_at: now,
            status: 'draft',
            partner_name: `${campaign.partner_name}`,
            campaign_name: campaign.campaign_name ? `${campaign.campaign_name} (Copy)` : '',
            deep_link_path: `${campaign.deep_link_path}-copy`
          }

          docRef.set(newCampaign)
          .then(() => {
            this.$buefy.toast.open({type: 'is-success', message: 'Campaign duplicated successfully!'})
            this.$router.push({ name: 'PartnerCampaignView', params: { id: docRef.id } })
          })
          .catch((error) => {
            this.$buefy.toast.open({type: 'is-danger', message: `Error duplicating campaign: ${error}`})
          });
        }
      })
    },
    deleteCampaign(campaign) {
      this.$buefy.dialog.confirm({
        title: 'Delete Campaign',
        message: `Are you sure you want to delete <b>"${campaign.partner_name}"</b>?<br /><br />This action cannot be undone.`,
        confirmText: 'Delete Campaign',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          db.collection("partner_campaigns").doc(campaign.id)
          .delete()
          .then(() => {
            this.$buefy.toast.open({type: 'is-success', message: 'Campaign successfully deleted!'})
          }).catch((error) => {
            this.$buefy.toast.open({type: 'is-danger', message: `Error deleting campaign: ${error}`})
          });
        }
      })
    }
  }
}
</script>

<style scoped>
.date-tag {
  font-size: 12px;
  color: var(--text-secondary);
}

.deep-link-code {
  background-color: var(--bg-tertiary);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.9em;
  color: var(--text-secondary);
}
</style>
